import { useQueries } from "@tanstack/react-query";
import { StatCard } from "@/components/admin/payouts/primitives";
import type { PaginatedClients } from "@/types";
import type { TypeFilter } from "./types";

const TYPES: TypeFilter[] = ["all", "company", "independent"];

/** Lightweight per-type count queries (limit=1) feeding the stat cards. */
export function useClientCounts() {
  const queries = useQueries({
    queries: TYPES.map((type) => ({
      queryKey: ["admin", "clients", "count", type],
      queryFn: async (): Promise<number> => {
        const res = await fetch(`/api/clients?page=1&limit=1&type=${type}`);
        if (!res.ok) throw new Error("Failed to fetch client count");
        const data = (await res.json()) as PaginatedClients;
        return data.total;
      },
    })),
  });
  return {
    all: queries[0]?.data,
    company: queries[1]?.data,
    independent: queries[2]?.data,
  };
}

export function ClientsStats({
  counts,
}: {
  counts: ReturnType<typeof useClientCounts>;
}) {
  if (counts.all === undefined) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl border border-border/60 bg-card px-4 py-3.5 shadow-sm sm:px-5 sm:py-4"
          >
            <div className="h-3.5 w-20 animate-pulse rounded bg-muted" />
            <div className="mt-2.5 h-7 w-14 animate-pulse rounded bg-muted/60 sm:h-8" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      <StatCard label="Ukupno" value={String(counts.all)} tone="primary" />
      <StatCard label="Firmski" value={String(counts.company ?? "—")} />
      <StatCard label="Samostalni" value={String(counts.independent ?? "—")} />
    </div>
  );
}
